import React, { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "react-toastify";
import Select from "react-select";
import { FaTable } from "react-icons/fa";
import axiosInstance from "../config/axios";
import ProgressTrackerModal from "./ProgressTrackerModal";
import ProgressReportModal from "./ProgressReportModal";
import ProgressTrackerLoginModal from "./ProgressTrackerLoginModal";
import { enrichTradesWithProgressReportEftDates } from "../utils/progressReportEftDates";
import {
  compareBaseTradeNumberDesc,
  progressReportTradeLabel,
} from "../utils/progressReportUtils";
import { isProgressTrackerAuthenticated } from "../utils/progressTrackerAuth";

function tradeLabel(t) {
  return progressReportTradeLabel(t);
}

const tradeSelectStyles = {
  control: (base) => ({
    ...base,
    minHeight: "38px",
    fontSize: "0.875rem",
    borderColor: "#d1d5db",
    boxShadow: "none",
    "&:hover": { borderColor: "#1d4ed8" },
  }),
  menu: (base) => ({ ...base, fontSize: "0.875rem", zIndex: 20 }),
};

const ProgressTrackerCard = () => {
  const [trades, setTrades] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [selectedId, setSelectedId] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [isAuthed, setIsAuthed] = useState(() => isProgressTrackerAuthenticated());
  const [loginOpen, setLoginOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);

  const loadTrades = useCallback(async () => {
    setLoadingList(true);
    try {
      const res = await axiosInstance.get("/trades");
      setTrades(res.data || []);
    } catch (e) {
      console.error(e);
      toast.error("Could not load trades for progress tracker.");
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => {
    loadTrades();
  }, [loadTrades]);

  const tradeOptions = useMemo(
    () =>
      trades
        .map((t) => ({
          value: t._id,
          label: tradeLabel(t),
          sortKey: String(t.tradeNumber ?? t.keyInfo?.tradeNumber ?? "").trim(),
        }))
        .sort((a, b) => compareBaseTradeNumberDesc(a.sortKey, b.sortKey)),
    [trades],
  );

  const selectedTradeOption = useMemo(
    () => tradeOptions.find((o) => o.value === selectedId) || null,
    [tradeOptions, selectedId],
  );

  const requireAuth = (action) => {
    if (isAuthed) {
      action();
      return;
    }
    setPendingAction(() => action);
    setLoginOpen(true);
  };

  const handleLoginSuccess = () => {
    setIsAuthed(true);
    if (pendingAction) {
      pendingAction();
      setPendingAction(null);
    }
  };

  const handleTradeSelect = (option) => {
    if (!isAuthed) {
      setPendingAction(() => () => {
        const id = option?.value || "";
        setSelectedId(id);
        if (id) setModalOpen(true);
      });
      setLoginOpen(true);
      return;
    }
    const id = option?.value || "";
    setSelectedId(id);
    if (id) setModalOpen(true);
    else setModalOpen(false);
  };

  const handleOpenClick = () => {
    requireAuth(() => {
      if (selectedId) setModalOpen(true);
    });
  };

  const handleProgressReport = () => {
    requireAuth(async () => {
      setReportLoading(true);
      setReportModalOpen(true);
      try {
        const [tradesRes, ctRes, reRes, ledgerRes] = await Promise.all([
          axiosInstance.get("/trades", { params: { progressReport: "1" } }),
          axiosInstance.get("/commission-trust-eft"),
          axiosInstance.get("/real-estate-trust-eft"),
          axiosInstance.get("/ledger", {
            params: { from: "2000-01-01", to: "2099-12-31" },
          }),
        ]);
        const ledger10004 = (ledgerRes.data || []).filter(
          (line) => String(line.accountNumber) === "10004",
        );
        const enriched = enrichTradesWithProgressReportEftDates(
          tradesRes.data || [],
          ctRes.data || [],
          reRes.data || [],
          ledger10004,
        );
        setTrades(enriched);
      } catch (e) {
        console.error(e);
        toast.error("Could not load trades for progress report.");
        setReportModalOpen(false);
      } finally {
        setReportLoading(false);
      }
    });
  };

  return (
    <div className="xl:flex-1 bg-white rounded-lg shadow-md p-6 min-w-0 flex flex-col relative">
      <h2 className="text-2xl font-semibold mb-2 text-gray-700">
        Progress Tracker
      </h2>
      <p className="text-xs text-gray-500 mb-4">
        Select a trade to open the full checklist. Checklist and milestone dates
        save on the trade; finalized date and EFT dates still come from Trade
        Key Info and trust EFTs.
      </p>

      <div
        className={`flex flex-col flex-1 ${!isAuthed ? "opacity-40 pointer-events-none select-none" : ""}`}
      >
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Trade
        </label>
        <Select
          inputId="progress-tracker-trade-select"
          options={tradeOptions}
          value={selectedTradeOption}
          onChange={handleTradeSelect}
          isClearable
          isSearchable
          isDisabled={loadingList || !isAuthed}
          placeholder="Search or select a trade…"
          noOptionsMessage={() => "No matching trades"}
          styles={tradeSelectStyles}
          className="mb-3"
          classNamePrefix="progress-tracker-trade"
        />

        {selectedId && !modalOpen && (
          <button
            type="button"
            onClick={handleOpenClick}
            className="w-full py-2 rounded-md bg-blue-900 text-white text-sm font-medium hover:bg-blue-950"
          >
            Open progress tracker
          </button>
        )}

        {!selectedId && !loadingList && (
          <p className="text-sm text-gray-500 mt-1">
            Choose a trade to view and edit the complete progress checklist.
          </p>
        )}

        <button
          type="button"
          onClick={handleProgressReport}
          disabled={loadingList || !isAuthed}
          className="mt-4 w-full flex items-center justify-center gap-2 py-2 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-60"
        >
          <FaTable />
          Progress Report
        </button>
      </div>

      {!isAuthed && (
        <div className="absolute inset-0 flex flex-col items-center justify-center p-6 pointer-events-none">
          <p className="text-sm text-gray-600 text-center mb-4 max-w-xs">
            Progress Tracker and Progress Report require login.
          </p>
          <button
            type="button"
            onClick={() => setLoginOpen(true)}
            className="pointer-events-auto px-6 py-2 rounded-md bg-blue-900 text-white text-sm font-medium hover:bg-blue-950 shadow-md"
          >
            Login
          </button>
        </div>
      )}

      <ProgressTrackerLoginModal
        isOpen={loginOpen}
        onClose={() => {
          setLoginOpen(false);
          setPendingAction(null);
        }}
        onSuccess={handleLoginSuccess}
      />

      <ProgressTrackerModal
        isOpen={modalOpen && isAuthed}
        tradeId={selectedId}
        onClose={() => setModalOpen(false)}
      />

      <ProgressReportModal
        isOpen={reportModalOpen && isAuthed}
        onClose={() => setReportModalOpen(false)}
        trades={trades}
        loading={reportLoading}
        onTradesSaved={async () => {
          await loadTrades();
        }}
      />
    </div>
  );
};

export default ProgressTrackerCard;
